import { appEmitter } from '@backrs_ui/grow-ui/src/core/EventEmitter';

import { getBackrsToken } from '../tokenHelpers';

/**
 * A wrapper around the native fetch function
 * - Automatically adds Authorization header (if token exists).
 * - Adds default Content-Type and Accept headers for JSON.
 * - Intercepts 503 responses to check for maintenance mode code.
 * - Dispatches setMaintenanceMode(true) to Redux if maintenance mode is detected.
 * - IMPORTANT: Always returns the original Response object, allowing the caller
 *   to handle response.ok, other errors, and data parsing.
 *
 * @param {RequestInfo | URL} url The URL to fetch.
 * @param {RequestInit | undefined} options Fetch options (method, headers, body, etc.).
 * @returns {Promise<Response>} A promise that resolves with the Response object.
 */
export async function fetchWrapper(
  url: RequestInfo | URL,
  options?: (RequestInit & { skipAuth?: boolean }) | undefined,
): Promise<Response> {
  try {
    const token = getBackrsToken();
    // Prepare headers
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token && !options?.skipAuth ? { Authorization: `Bearer ${token}` } : {}),
    };

    const mergedOptions: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options?.headers,
      },
    };

    const response = await fetch(url, mergedOptions);

    if (response.status === 503) {
      // Clone is needed if multiple places might read the body (like debug tools or future interceptors)
      // It's safer to keep it, even if only this code reads it right now.
      const clonedResponse = response.clone();

      try {
        const body = await clonedResponse.json();

        if (body?.code === 'MAINTENANCE_MODE_ENABLED') {
          console.warn(
            'Maintenance mode detected via fetch wrapper, emitting maintenance mode event.',
          );
          appEmitter.emit('maintenanceModeDetected');
          // NOTE: We DO NOT throw here. We still return the response below.
        }
      } catch (e) {
        // Log error checking body, but proceed to return the original response
        console.error('Error checking 503 response body in fetch wrapper:', e);
      }
    }

    // Always return the response object, allowing the caller to handle it
    return response;
  } catch (error) {
    // Handle network errors etc.
    console.error('Fetch wrapper caught network or other error:', error);
    // Re-throw the error so the original caller's .catch block is triggered
    throw error;
  }
}
